// Binghamton University (State University of New York at Binghamton): public
// college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Binghamton's Fall 2025 Common Data Set, the file the Office of
//    Institutional Research links as "Common Data Set 2025" (a Google Drive
//    PDF; the SOURCES url is the binghamton.edu page that links it). Quirk:
//    the PDF's running header still reads "Common Data Set 2024-2025", but
//    its contents are the 2025-2026 edition: Fall 2025 applicants, the test
//    policy for Fall 2027 and 2026-2027 costs. C1 applicants, admits and
//    enrollees are the sums of the CDS's own by-sex totals (61,197 applied,
//    22,835 admitted, 3,201 enrolled); the C1 residency table on the same
//    page totals slightly less (61,188 applied, 22,832 admitted), and its
//    "unknown" column (229 applied, 178 admitted) is not shown. C8 marks the
//    SAT or ACT "required for some" for Fall 2027; the admissions FAQ says
//    some joint early assurance programs require scores. C21/C22 plans.
//  - Binghamton Undergraduate Admissions: First-year Applicants: Get Started
//    Here (deadlines, decision dates, School of the Arts portfolio and
//    audition table with 2027 audition dates, so the page is for Fall 2027
//    entry), Binghamton's Test Policies, Test-optional FAQs.
//  - Binghamton Financial Aid, Cost of Attendance, 2026-27 Undergraduate
//    Estimated Cost of Attendance (living on or off campus). It is used
//    instead of CDS G1/G5 because the CDS leaves food and housing blank.
//    Quirk: the CDS lists out-of-state tuition as $28,970; the financial aid
//    page, dated "As of June 2026", lists $29,840, which is used here.
//  - College Scorecard, school id 196079 (net price by family income), read
//    from the Scorecard API on 2026-10-01; the Scorecard names the school
//    "Binghamton University".
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Binghamton Common Data Set 2025-2026 (Fall 2025)',
        url: 'https://www.binghamton.edu/offices/oir/institutional-data/common-data-set.html',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Binghamton Undergraduate Admissions, First-year Applicants: Get Started Here',
        url: 'https://www.binghamton.edu/admissions/undergraduate/apply/freshman/',
        read: '2026-10-01',
    },
    testPolicy: {
        label: 'Binghamton Undergraduate Admissions, Binghamton\'s Test Policies',
        url: 'https://www.binghamton.edu/admissions/undergraduate/info/test-policy.html',
        read: '2026-10-01',
    },
    testFaq: {
        label: 'Binghamton Undergraduate Admissions, Test-optional FAQs',
        url: 'https://www.binghamton.edu/admissions/undergraduate/info/test-optional-faqs.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Binghamton Financial Aid, Cost of Attendance',
        url: 'https://www.binghamton.edu/financial-aid/cost.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196079-Binghamton-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'state-university-of-new-york-at-binghamton',
    kbId: 'state_university_of_new_york_at_binghamton',
    name: 'Binghamton University',
    shortName: 'Binghamton',
    aliases: ['SUNY Binghamton', 'State University of New York at Binghamton'],
    location: 'Binghamton, NY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Binghamton brand guidelines, colors); decoration only.
    branding: {
        primary: '#005A43',
        secondary: '#005A43',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/state_university_of_new_york_at_binghamton_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 61197,
        admitted: 22835,
        enrolled: 3201,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 49217, admitted: 14324 },
            { group: 'Out-of-state', applied: 8932, admitted: 6586 },
            { group: 'International', applied: 2810, admitted: 1744 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (229 applicants of unknown residency are not shown)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Binghamton\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early action',
        rows: [
            { plan: 'Early Action', applied: 34283, admitted: 14315 },
        ],
        note: 'Early action counts for the Fall 2025 entering class, as the Common Data Set reports them. They are part of the totals above. Binghamton has no early decision plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most applicants apply for a BA or BS program. The School of the Arts degrees ask for more:',
        rows: [
            { school: 'BFA Art & Design', requirement: 'A portfolio, uploaded through the application status checker. Priority: apply by December 1, portfolio by December 10. Regular: apply by January 15, portfolio by January 25.' },
            { school: 'BFA Musical Theatre', requirement: 'A portfolio and an audition by invitation. Priority: apply by December 1, materials and portfolio by December 10. Regular: apply by January 15, materials by January 25. Priority-tier auditions are on campus February 6, 2027 and in New York City February 13, 2027; regular-tier auditions are on a space-available basis.' },
            { school: 'BMus Music Performance', requirement: 'A portfolio and an audition by invitation. Priority: apply by December 1, materials and portfolio by December 10. Regular: apply by January 15, materials by January 25. Priority-tier auditions are in New York City February 20, 2027 and on campus February 26-28, 2027; regular-tier auditions are on a space-available basis.' },
            { school: 'Joint early assurance programs', requirement: 'Some require SAT or ACT scores even though Binghamton is test-optional; Binghamton asks you to email admit@binghamton.edu if you are interested.', source: 'testFaq' },
        ],
        note: 'BFA and BMus applicants hear about direct admission to those majors on a rolling basis, no later than April 1. EOP applicants are not eligible for direct admission to BFA and BMus programs and cannot apply early action.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 15', binding: false },
            { plan: 'Regular admission', deadline: 'January 15', decision: 'By April 1', binding: false },
        ],
        note: 'For early action, transcripts and any test scores are due December 1. For regular admission, everything is due January 15; applications received after the deadline are reviewed on a space-available basis. You may apply through the Common App, the SUNY Application or the Coalition Application, and the application fee is $50.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Binghamton is test-optional: SAT and ACT scores are not required. On the application you say whether you want your scores included in the review, and that choice cannot be changed once you apply. If you send scores, Binghamton combines your highest section scores within the SAT or within the ACT, not across the two. You may self-report scores in the status checker, and enrolling students then send official scores by May 15.',
        source: 'testPolicy',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some applicants for fall 2027; the admissions FAQ says some joint early assurance programs require scores.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7070, 29840] },
            { item: 'Fees', amounts: [3497, 3497] },
            { item: 'Housing and food', amounts: [20179, 20179] },
            { item: 'Books', amounts: [1000, 1000] },
            { item: 'Transportation', amounts: [250, 450] },
            { item: 'Personal expenses', amounts: [750, 750] },
        ],
        totals: [32746, 55716],
        note: 'Binghamton\'s estimate for an undergraduate living on or off campus. Housing and food are the median housing plan and meal plan C; tuition is as of June 2026 and subject to change.',
        source: 'costs',
        netPrice: {
            average: 20907,
            byIncome: [
                { income: '$0 to $30,000', amount: 11463 },
                { income: '$30,001 to $48,000', amount: 13445 },
                { income: '$48,001 to $75,000', amount: 18397 },
                { income: '$75,001 to $110,000', amount: 22349 },
                { income: 'Over $110,000', amount: 28293 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Binghamton\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
